// Splide を使ったメインビジュアルスライダー。
//
// ============================================
// HTML 構造例:
// ============================================
// <div class="splide js-slider-mv">...</div>
// <div class="splide js-slider-mv-thumbs">...</div>
// ============================================
// CSS 注意:
// is-active / is-open 等の表示・トランジションは CSS 側で定義する。

import Splide from '@splidejs/splide';
import '@splidejs/splide/css/core';

import { bindOnce } from '../utils/bindOnce';

// 読み上げ用の文言。指定しないと英語のまま出力される
const I18N_JA = {
  prev: '前のスライドへ',
  next: '次のスライドへ',
  first: '最初のスライドへ',
  last: '最後のスライドへ',
  slideX: 'スライド %s へ',
  pageX: '%s ページ目へ',
  play: '自動再生を開始',
  pause: '自動再生を停止',
  carousel: 'カルーセル',
  slide: 'スライド',
  select: '表示するスライドを選択',
  slideLabel: '%s / %s',
};

const MV_MOTION = {
  interval: 5000,
  speed: 1000,
  easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

const DATA_SOURCES = [
  {
    selector: '.splide.js-slider-mv',
    options: {
      type: 'loop',
      autoplay: true,
      ...MV_MOTION,
      perPage: 1,
      perMove: 1,
      // デザイン実寸: SP 375px → 余白15px・gap5px / PC 1440px → 余白177px・gap20px
      padding: '4%',
      gap: '5px',
      arrows: false,
      pagination: false,
      updateOnMove: true,
      mediaQuery: 'min',
      breakpoints: {
        1024: {
          // スライド幅 = コンテナ幅 - 余白×2。余白を max() にすることで
          // 1440px までは 12.29%（デザイン実寸）で可変し、それ以上ではスライドが
          // 1086px で頭打ちになり、余った幅がすべて左右の隣スライドの見え幅に回る。
          padding: 'max(12.29%, (100% - 1086px) / 2)',
          gap: '20px',
        },
      },
    },
    thumbnails: {
      selector: '.splide.js-slider-mv-thumbs',
      options: {
        type: 'slide',
        isNavigation: true,
        perPage: 4.5,
        perMove: 1,
        gap: '5px',
        arrows: false,
        pagination: false,
        drag: false,
        mediaQuery: 'min',
        breakpoints: {
          1024: {
            fixedWidth: '100px',
            perPage: 6.5,
            gap: '8px',
          },
        },
      },
    },
  },
];

const CONFIG = {
  i18n: I18N_JA,                         // Splide の読み上げ文言
  dataSources: DATA_SOURCES,             // スライダー定義一覧
  progressProperty: '--slider-progress', // サムネイル進捗の CSS 変数名
};

// 動きを減らす設定の環境では自動再生しない（ドラッグとサムネイル操作は残す）
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// DATA_SOURCES の定義に基づいてスライダーを初期化する
function slider() {
  CONFIG.dataSources.forEach(({ selector, options, thumbnails }) => {
    const targets      = document.querySelectorAll(selector);
    const thumbTargets = thumbnails ? document.querySelectorAll(thumbnails.selector) : [];

    targets.forEach((target, index) => {
      if (!bindOnce(target, 'splideMount')) return;

      const main = new Splide(target, {
        i18n: CONFIG.i18n,
        ...options,
        autoplay: Boolean(options.autoplay) && !prefersReducedMotion,
      });
      const thumbTarget = thumbTargets[index];

      if (!thumbTarget) {
        main.mount();
        return;
      }

      // sync() は mount() より前に呼ぶ必要がある
      const thumb = new Splide(thumbTarget, { i18n: CONFIG.i18n, ...thumbnails.options });
      main.sync(thumb);

      // Splide が切り替え時に rate 0 を再通知するため、個別のリセットは不要。
      main.on('autoplay:playing', (rate) => {
        thumbTarget.style.setProperty(CONFIG.progressProperty, rate);
      });

      main.mount();
      thumb.mount();
    });
  });
}

export { slider };
